import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertOctagon,
  Calendar,
  Camera,
  ChevronLeft,
  Clock,
  ExternalLink,
  MapPin,
  MessageSquare,
  Package,
  Phone,
  Route,
  Send,
  User as UserIcon,
} from 'lucide-react';
import type { JobStatus, Urgency, VehicleRequired } from '@godrop/types';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { StatusChip } from '../components/ui/status-chip';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { useToast } from '../lib/toast';
import { fmtDate, fmtMoney, fmtMoney2, fmtRelative } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { EmptyState } from '../components/ui/empty-state';

interface AdminJob {
  id: string;
  code: string;
  poster_id: string;
  title: string;
  status: JobStatus;
  vehicle_required: VehicleRequired;
  urgency: Urgency;
  payout: number;
  pickup_area: string;
  pickup_address: string;
  pickup_lat: number;
  pickup_lng: number;
  pickup_window_start: string;
  pickup_window_end: string;
  dropoff_area: string;
  dropoff_address: string;
  dropoff_lat: number;
  dropoff_lng: number;
  delivery_deadline: string;
  contact_method: string;
  contact_value: string;
  item_type: string;
  item_size: string | null;
  item_quantity: number;
  item_weight_kg: number | null;
  fragile: boolean;
  special_instructions: string | null;
  parcel_photo_urls: string[] | null;
  estimated_distance_km: number | null;
  estimated_duration_min: number | null;
  pod_image_urls: string[];
  pod_uploaded_at: string | null;
  accepted_by_user_id: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;

  poster_phone: string;
  poster_name: string | null;
  poster_reliability_score: number;
  poster_completed_count: number;
  courier_phone: string | null;
  courier_name: string | null;
  courier_profile_image_url: string | null;
  courier_vehicle_mode: string | null;

  ping_count: number;
  rating_count: number;
}

interface Ping {
  id: string;
  user_id: string;
  ping_method: string;
  wave: number;
  sent_at: string;
  delivered_at: string | null;
  opened_at: string | null;
  user_phone: string;
  user_name: string | null;
  user_vehicle_mode: string | null;
}

interface Rating {
  id: string;
  rater_id: string;
  ratee_id: string;
  role_of_ratee: 'poster' | 'courier';
  stars: number;
  comment: string | null;
  created_at: string;
  rater_phone: string;
  rater_name: string | null;
  ratee_phone: string;
  ratee_name: string | null;
}

function initials(s: string | null | undefined): string {
  if (!s) return '?';
  const parts = s.trim().split(/\s+/).filter(Boolean);
  return parts.length === 1
    ? parts[0]!.slice(0, 2).toUpperCase()
    : (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();

  const [job, setJob] = useState<AdminJob | null>(null);
  const [pings, setPings] = useState<Ping[] | null>(null);
  const [ratings, setRatings] = useState<Rating[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [photo, setPhoto] = useState<string | null>(null);

  function load() {
    if (!id) return;
    setJob(null);
    Promise.all([
      api<AdminJob>(`/api/admin/jobs/${id}`),
      api<{ pings: Ping[] }>(`/api/admin/jobs/${id}/pings`),
      api<{ ratings: Rating[] }>(`/api/admin/jobs/${id}/ratings`),
    ])
      .then(([j, p, r]) => {
        setJob(j);
        setPings(p.pings);
        setRatings(r.ratings);
      })
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [id]);

  async function cancel() {
    if (!id) return;
    try {
      await api(`/api/admin/jobs/${id}/cancel`, { method: 'POST', json: {} });
      toast.show('Drop cancelled', 'success');
      load();
    } catch (e) {
      toast.show(e instanceof ApiError ? `Cancel failed (${e.status})` : 'Cancel failed', 'error');
    }
  }

  if (!id) return null;
  if (err) {
    return (
      <Shell title="Drop">
        <Card className="border-error/40 bg-error-tint">
          <CardContent className="py-4 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      </Shell>
    );
  }
  if (!job) {
    return (
      <Shell title="Drop">
        <div className="space-y-3">
          <Skeleton className="h-8 w-72" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </Shell>
    );
  }

  const canCancel = job.status === 'open' || job.status === 'accepted';

  return (
    <Shell title={`Drop · ${job.code}`} subtitle={job.title}>
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <Button asChild variant="ghost" size="sm" className="-ml-3 mb-1 h-7 px-2 text-text-muted">
            <Link to="/jobs">
              <ChevronLeft className="h-4 w-4" />
              All drops
            </Link>
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-2xl font-bold tracking-tight text-text">{job.title}</h2>
            <span className="tnum rounded-md bg-surface-muted px-2 py-0.5 text-xs font-semibold text-text-subtle">
              {job.code}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <StatusChip status={job.status} />
            {job.urgency === 'urgent' && <Badge variant="urgent">urgent</Badge>}
            <Badge>{job.vehicle_required}</Badge>
            <Badge>{job.item_type}</Badge>
            {job.fragile && <Badge variant="urgent">fragile</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="tnum text-3xl font-bold tracking-tight text-brand-700 dark:text-brand-500">
            {fmtMoney2(job.payout)}
          </span>
          {canCancel && (
            <ConfirmDialog
              title="Cancel this drop?"
              description={
                job.status === 'accepted'
                  ? 'A courier has already accepted. Cancelling will surprise both parties.'
                  : 'This drop will be removed from the open feed.'
              }
              confirmLabel="Cancel drop"
              cancelLabel="Keep drop"
              onConfirm={cancel}
              trigger={
                <Button variant="danger" size="sm">
                  <AlertOctagon className="h-4 w-4" /> Cancel drop
                </Button>
              }
            />
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-4 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Logistics</CardTitle>
              <CardDescription>Pickup, drop, and route</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                    <MapPin className="mr-1 inline h-3 w-3" />
                    Pickup
                  </p>
                  <p className="mt-1 text-sm font-medium text-text">{job.pickup_area}</p>
                  <p className="mt-0.5 text-sm text-text-subtle">{job.pickup_address}</p>
                  <p className="tnum mt-1 text-xs text-text-muted">
                    {job.pickup_lat.toFixed(5)}, {job.pickup_lng.toFixed(5)}
                  </p>
                  <p className="mt-2 text-xs text-text-muted">
                    <Clock className="mr-1 inline h-3 w-3" />
                    {fmtDate(job.pickup_window_start)} → {fmtDate(job.pickup_window_end)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                    <MapPin className="mr-1 inline h-3 w-3" />
                    Dropoff
                  </p>
                  <p className="mt-1 text-sm font-medium text-text">{job.dropoff_area}</p>
                  <p className="mt-0.5 text-sm text-text-subtle">{job.dropoff_address}</p>
                  <p className="tnum mt-1 text-xs text-text-muted">
                    {job.dropoff_lat.toFixed(5)}, {job.dropoff_lng.toFixed(5)}
                  </p>
                  <p className="mt-2 text-xs text-text-muted">
                    <Calendar className="mr-1 inline h-3 w-3" />
                    deadline {fmtDate(job.delivery_deadline)}
                  </p>
                </div>
              </div>
              <Separator />
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="inline-flex items-center gap-1 text-text-subtle">
                  <Route className="h-4 w-4" />
                  <span className="tnum">
                    {job.estimated_distance_km != null ? `${job.estimated_distance_km.toFixed(1)} km` : '—'}
                  </span>
                </span>
                <span className="inline-flex items-center gap-1 text-text-subtle">
                  <Clock className="h-4 w-4" />
                  <span className="tnum">
                    {job.estimated_duration_min != null ? `${job.estimated_duration_min} min` : '—'}
                  </span>
                </span>
                <Button asChild variant="ghost" size="sm" className="ml-auto">
                  <a
                    href={`https://www.google.com/maps/dir/${job.pickup_lat},${job.pickup_lng}/${job.dropoff_lat},${job.dropoff_lng}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Maps <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Item</CardTitle>
              <CardDescription>What the courier picks up</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Field label="Type" value={job.item_type} />
                <Field label="Size" value={job.item_size ?? '—'} />
                <Field label="Quantity" value={String(job.item_quantity)} mono />
                <Field
                  label="Weight"
                  value={job.item_weight_kg != null ? `${job.item_weight_kg} kg` : '—'}
                  mono
                />
              </div>
              {job.special_instructions && (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                    Instructions
                  </p>
                  <p className="mt-1 whitespace-pre-wrap rounded-md bg-surface-muted p-3 text-sm text-text-subtle">
                    {job.special_instructions}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Photos</CardTitle>
              <CardDescription>
                Parcel photos (up to 5) and POD image
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                  <Package className="mr-1 inline h-3 w-3" />
                  Parcel ({job.parcel_photo_urls?.length ?? 0})
                </p>
                {(job.parcel_photo_urls?.length ?? 0) === 0 ? (
                  <p className="text-sm text-text-muted">No parcel photos.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {job.parcel_photo_urls!.map((u) => (
                      <button
                        key={u}
                        onClick={() => setPhoto(u)}
                        className="h-20 w-20 overflow-hidden rounded-md border border-border bg-surface-muted transition-shadow hover:shadow-active"
                      >
                        <img src={u} alt="parcel" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Separator />
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-text-muted">
                  <Camera className="mr-1 inline h-3 w-3" />
                  POD
                </p>
                {(job.pod_image_urls ?? []).length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {(job.pod_image_urls ?? []).map((url, idx) => (
                      <button
                        key={url}
                        onClick={() => setPhoto(url)}
                        className="block h-32 w-32 overflow-hidden rounded-md border border-border bg-surface-muted transition-shadow hover:shadow-active"
                      >
                        <img src={url} alt={`POD ${idx + 1}`} className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-text-muted">No POD uploaded.</p>
                )}
                {job.pod_uploaded_at && (
                  <p className="mt-2 text-xs text-text-muted">
                    Uploaded {fmtDate(job.pod_uploaded_at)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ping fanout</CardTitle>
              <CardDescription>{job.ping_count} pings sent · most recent first</CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {!pings && <Skeleton className="mx-5 mb-5 h-32 w-[calc(100%-2.5rem)]" />}
              {pings && pings.length === 0 && (
                <EmptyState icon={Send} title="No pings sent yet." />
              )}
              {pings && pings.length > 0 && (
                <ul className="divide-y divide-border">
                  {pings.map((p) => (
                    <li key={p.id} className="flex items-center gap-3 px-5 py-3 text-sm">
                      <Badge>wave {p.wave}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-text">
                          <Link to={`/users/${p.user_id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                            {p.user_name ?? p.user_phone}
                          </Link>
                        </p>
                        <p className="tnum truncate text-xs text-text-muted">
                          {p.user_phone} · {p.user_vehicle_mode ?? '—'}
                        </p>
                      </div>
                      <div className="text-right text-xs text-text-muted">
                        <p className="font-medium text-text-subtle">{p.ping_method}</p>
                        <p>{fmtRelative(p.sent_at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Ratings</CardTitle>
              <CardDescription>{job.rating_count} ratings on this drop</CardDescription>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {!ratings && <Skeleton className="mx-5 mb-5 h-16 w-[calc(100%-2.5rem)]" />}
              {ratings && ratings.length === 0 && <EmptyState title="No ratings yet." />}
              {ratings && ratings.length > 0 && (
                <ul className="divide-y divide-border">
                  {ratings.map((r) => (
                    <li key={r.id} className="px-5 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <Stars value={r.stars} />
                        <Badge>{r.role_of_ratee} rated</Badge>
                        <span className="ml-auto text-xs text-text-muted">{fmtRelative(r.created_at)}</span>
                      </div>
                      {r.comment && <p className="mt-1 text-text-subtle">"{r.comment}"</p>}
                      <p className="mt-1 text-xs text-text-muted">
                        <Link to={`/users/${r.rater_id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                          {r.rater_name ?? r.rater_phone}
                        </Link>
                        {' → '}
                        <Link to={`/users/${r.ratee_id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                          {r.ratee_name ?? r.ratee_phone}
                        </Link>
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Poster</CardTitle>
            </CardHeader>
            <CardContent>
              <Link
                to={`/users/${job.poster_id}`}
                className="group flex items-center gap-3"
              >
                <Avatar><AvatarFallback>{initials(job.poster_name ?? job.poster_phone)}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text group-hover:text-brand-700 dark:group-hover:text-brand-500">
                    {job.poster_name ?? '—'}
                  </p>
                  <p className="tnum truncate text-xs text-text-muted">{job.poster_phone}</p>
                </div>
              </Link>
              <Separator className="my-3" />
              <div className="grid grid-cols-2 gap-3 text-xs">
                <Field label="Reliability" value={job.poster_reliability_score.toFixed(1)} mono />
                <Field label="Completed" value={String(job.poster_completed_count)} mono />
              </div>
              <Separator className="my-3" />
              <div className="space-y-2 text-xs">
                <p className="text-text-muted">
                  <span className="font-medium uppercase tracking-wider">Contact</span>
                </p>
                <p className="flex items-center gap-2 text-text-subtle">
                  {job.contact_method === 'phone' && <Phone className="h-3 w-3" />}
                  {job.contact_method === 'whatsapp' && <MessageSquare className="h-3 w-3" />}
                  {job.contact_method === 'telegram' && <Send className="h-3 w-3" />}
                  <span className="tnum">{job.contact_value}</span>
                  <Badge className="ml-auto">{job.contact_method}</Badge>
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Courier</CardTitle>
            </CardHeader>
            <CardContent>
              {!job.accepted_by_user_id ? (
                <EmptyState icon={UserIcon} title="Not yet accepted." />
              ) : (
                <>
                  <Link
                    to={`/users/${job.accepted_by_user_id}`}
                    className="group flex items-center gap-3"
                  >
                    <Avatar>
                      {job.courier_profile_image_url && (
                        <AvatarImage src={job.courier_profile_image_url} alt="courier" />
                      )}
                      <AvatarFallback>{initials(job.courier_name ?? job.courier_phone)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text group-hover:text-brand-700 dark:group-hover:text-brand-500">
                        {job.courier_name ?? '—'}
                      </p>
                      <p className="tnum truncate text-xs text-text-muted">{job.courier_phone}</p>
                    </div>
                  </Link>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {job.courier_vehicle_mode && <Badge>{job.courier_vehicle_mode}</Badge>}
                  </div>
                  {job.accepted_at && (
                    <p className="mt-3 text-xs text-text-muted">
                      Accepted {fmtRelative(job.accepted_at)}
                    </p>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <Timeline label="Created" at={job.created_at} done />
              <Timeline label="Accepted" at={job.accepted_at} done={!!job.accepted_at} />
              <Timeline label="POD uploaded" at={job.pod_uploaded_at} done={!!job.pod_uploaded_at} />
              <Timeline label="Updated" at={job.updated_at} done />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Snapshot</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3 text-xs">
              <Field label="Payout" value={fmtMoney(job.payout)} mono />
              <Field label="Distance" value={job.estimated_distance_km != null ? `${job.estimated_distance_km.toFixed(1)} km` : '—'} mono />
              <Field label="Duration" value={job.estimated_duration_min != null ? `${job.estimated_duration_min} min` : '—'} mono />
              <Field label="Pings" value={String(job.ping_count)} mono />
            </CardContent>
          </Card>
        </div>
      </div>

      {photo && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPhoto(null)}
          style={{ animation: 'admin-fade-in 200ms ease-out' }}
        >
          <img
            src={photo}
            alt="enlarged"
            className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-float"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Shell>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-text-muted">{label}</p>
      <p className={`mt-0.5 text-sm font-medium text-text ${mono ? 'tnum' : ''}`}>{value}</p>
    </div>
  );
}

function Timeline({ label, at, done }: { label: string; at: string | null; done: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={`h-2 w-2 shrink-0 rounded-full ${
          done ? 'bg-brand-600' : 'bg-border-emphasis'
        }`}
      />
      <span className="flex-1 text-text-subtle">{label}</span>
      <span className="text-xs text-text-muted">{at ? fmtRelative(at) : '—'}</span>
    </div>
  );
}

function Stars({ value }: { value: number }) {
  return (
    <span className="tnum text-sm font-semibold tracking-wide text-amber-600 dark:text-amber-400">
      {'★'.repeat(value)}
      <span className="text-text-muted">{'★'.repeat(5 - value)}</span>
    </span>
  );
}
